/**
 * Runtime context snapshot attached to every error report.
 *
 * This captures *metadata* about the app state at crash time — never secrets
 * (no tokens, keys, or message content).
 *
 * To keep the error-reporting pipeline lightweight and side-effect free — it
 * must be importable and runnable even when the rest of the app is broken — it
 * does NOT statically import Zustand stores (which transitively pull in the
 * socket / crypto graph). Instead the app registers a provider at startup via
 * {@link setErrorContextProvider}; if none is registered we still report the
 * dependency-free browser fields below.
 */

export type RuntimeContext = {
  isAuthenticated: boolean;
  wsConnectionStatus: string;
  networkStatus: string;
  activeChatId: string | null;
  viewport: string;
  deviceMemory?: number;
  language: string;
  online: boolean;
};

/** App state the host registers; merged over the browser-derived defaults. */
export type AppContext = Pick<
  RuntimeContext,
  "isAuthenticated" | "wsConnectionStatus" | "networkStatus" | "activeChatId"
>;

type ContextProvider = () => Partial<AppContext>;

let provider: ContextProvider | null = null;

export function setErrorContextProvider(fn: ContextProvider | null): void {
  provider = fn;
}

type UsernameProvider = () => string;

let usernameProvider: UsernameProvider | null = null;

/**
 * Registers how to resolve the reporter's username for the feedback envelope.
 * Lets the reporting pipeline stay free of store imports (see {@link ./reportError}).
 */
export function setReportUsernameProvider(fn: UsernameProvider | null): void {
  usernameProvider = fn;
}

export function resolveUsername(): string {
  if (usernameProvider) {
    try {
      return usernameProvider() || "";
    } catch {
      // A broken provider must never break reporting.
    }
  }
  return "";
}

type NavigatorWithMemory = Navigator & { deviceMemory?: number };

export function collectContext(): RuntimeContext {
  const nav =
    typeof navigator !== "undefined"
      ? (navigator as NavigatorWithMemory)
      : undefined;

  const base: RuntimeContext = {
    isAuthenticated: false,
    wsConnectionStatus: "unknown",
    networkStatus: "unknown",
    activeChatId: null,
    viewport:
      typeof window !== "undefined"
        ? `${window.innerWidth}x${window.innerHeight}`
        : "unknown",
    deviceMemory: nav?.deviceMemory,
    language: nav?.language ?? "unknown",
    online: nav ? nav.onLine : true,
  };

  if (provider) {
    try {
      return { ...base, ...provider() };
    } catch {
      // A broken provider must never break reporting — fall back to base.
    }
  }
  return base;
}
